import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { Plus, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { PageHeader } from '@/components/shared'
import { emailCampaignService } from '@/services/emailCampaignService'
import CampaignCard from '@/components/MarketingEmail/CampaignCard'

const timestamp = (campaign) => {
  const value = campaign.sent_at || campaign.scheduled_at || campaign.created_at
  return value ? new Date(value).getTime() : 0
}

const Campaigns = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [deletingCampaign, setDeletingCampaign] = useState(null)
  const [cancellingCampaign, setCancellingCampaign] = useState(null)

  const { data, isLoading } = useQuery('email-campaigns', emailCampaignService.getAllCampaigns, {
    onError: () => toast.error('Failed to fetch campaigns'),
    refetchInterval: (data) => (data?.data?.campaigns?.some((c) => c.status === 'SENDING') ? 3000 : false),
  })
  const campaigns = data?.data?.campaigns || []

  const filteredCampaigns = useMemo(() => {
    const sorted = [...campaigns].sort((a, b) => timestamp(b) - timestamp(a))
    if (!search.trim()) return sorted
    const term = search.trim().toLowerCase()
    return sorted.filter((c) => c.name.toLowerCase().includes(term))
  }, [campaigns, search])

  const refresh = () => queryClient.invalidateQueries('email-campaigns')

  const handleDelete = async () => {
    if (!deletingCampaign) return
    try {
      await emailCampaignService.deleteCampaign(deletingCampaign.id)
      toast.success('Campaign deleted')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete campaign')
    } finally {
      setDeletingCampaign(null)
    }
  }

  const handleCancel = async () => {
    if (!cancellingCampaign) return
    try {
      const res = await emailCampaignService.cancelCampaign(cancellingCampaign.id)
      toast.success(res?.message || 'Campaign cancelled')
      refresh()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to cancel campaign')
    } finally {
      setCancellingCampaign(null)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaigns"
        description="Send personalized email campaigns to your contacts"
        actions={
          <Button size="sm" onClick={() => navigate('/marketing-email/campaigns/new')}>
            <Plus className="mr-2 h-4 w-4" />
            New Campaign
          </Button>
        }
      />

      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search campaigns..."
          className="pl-9 bg-background"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[100px] w-full rounded-lg" />
          ))}
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20 text-center">
          <p className="text-sm font-semibold">
            {search ? 'No campaigns match your search' : 'No campaigns yet'}
          </p>
          <p className="mt-1.5 max-w-xs text-xs text-muted-foreground">
            {search ? 'Try a different search term.' : 'Create one to reach your contacts.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCampaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onEdit={(campaign) => navigate(`/marketing-email/campaigns/${campaign.id}/edit`)}
              onCancel={setCancellingCampaign}
              onDelete={setDeletingCampaign}
            />
          ))}
        </div>
      )}

      <AlertDialog open={!!deletingCampaign} onOpenChange={() => setDeletingCampaign(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingCampaign?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancellingCampaign} onOpenChange={() => setCancellingCampaign(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cancellingCampaign?.status === 'SENDING' ? 'Stop Send' : 'Cancel Campaign'}</AlertDialogTitle>
            <AlertDialogDescription>
              {cancellingCampaign?.status === 'SENDING'
                ? `"${cancellingCampaign?.name}" is currently sending. Recipients already emailed will not be un-sent, but remaining recipients will be skipped. Continue?`
                : `Are you sure you want to cancel "${cancellingCampaign?.name}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Never mind</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel}>
              {cancellingCampaign?.status === 'SENDING' ? 'Stop Send' : 'Cancel Campaign'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Campaigns
